import { createStore } from 'zustand/vanilla';
import { createId } from '@/core/id';
import { chatError } from '@/core/types';
import { toChatError } from '@/transport/errors';
import { Emitter } from '@/core/emitter';
import { createLogger } from '@/core/logger';
import { MOBILE_QUERY } from '@/hooks/useMediaQuery';
import { clearStoredId, clearTranscript, clearUiState, mergeTranscript, readStoredId, readTranscript, readUiState, storeId, writeTranscript, writeUiState, DEFAULT_STORAGE_NAMESPACE, } from './persistence';
/** Fixed so the local greeting can never be inserted twice. */
const GREETING_ID = 'local_greeting';
/** Streaming patches the store per token; storage only needs the settled state. */
const PERSIST_DEBOUNCE_MS = 250;
/**
 * One engine per widget instance. Owns the store, drives the transport and
 * translates `TransportEvent`s into state patches. All React components read
 * from here; none of them talk to a transport directly.
 */
export class ChatEngine {
    config;
    store;
    events = new Emitter();
    logger;
    transport;
    controller;
    lastUserText;
    connected = false;
    /** De-dupes concurrent init() calls so callers await one handshake. */
    initPromise;
    /** Set while the conversation is being mirrored into storage. */
    stopPersisting;
    /** When the conversation was last not silent — a send, or a reply landing. */
    lastActivityAt = Date.now();
    idleTimer;
    stopIdleWatch;
    /**
     * Bumped by reset(). Async work started under an older value — a reply in
     * flight, a resume half way through — belongs to a conversation the visitor
     * has abandoned, and must not write into the new one.
     */
    epoch = 0;
    /** A past chat to open on the next init (openConversation), instead of the stored one. */
    reopenId;
    /** Where this widget keeps what it persists (state/persistence.ts). */
    get ns() {
        return this.config.storageNamespace ?? DEFAULT_STORAGE_NAMESPACE;
    }
    constructor(config) {
        this.config = config;
        this.transport = config.transport;
        this.logger = createLogger({ level: config.logLevel, sink: config.onLog });
        // One `logLevel` governs the transport too.
        this.transport.setLogger?.(this.logger.scope('transport'));
        // Picks up where this tab's previous page left off. Not on phones: there
        // the panel is a full-screen sheet, and reopening it would hide the page
        // the visitor just navigated to.
        const restored = config.features.persistence ? readUiState(this.ns) : undefined;
        this.store = createStore(() => ({
            order: [],
            byId: {},
            status: 'idle',
            isSending: false,
            isAgentTyping: false, agentStatus: undefined, agentStatusText: undefined,
            isOpen: config.presentation === 'fullscreen' ||
                Boolean(config.openOnLoad) ||
                (restored?.isOpen === true && !isMobileViewport()),
            view: 'chat',
            unreadCount: 0,
            draft: restored?.draft ?? '',
            inactivity: 'live',
            pastChats: { status: 'idle', items: [] },
        }));
        if (config.features.persistence)
            this.startPersisting();
        if (config.inactivity.enabled)
            this.startIdleWatch();
    }
    /**
     * Mirrors the conversation and panel state into storage as they change, so
     * the next page can pick them up. Writes are batched, and flushed when the
     * page is being left — the one moment that must not be missed.
     */
    startPersisting() {
        let timer;
        const flush = () => {
            clearTimeout(timer);
            timer = undefined;
            this.persistNow();
        };
        const unsubscribe = this.store.subscribe((state, prev) => {
            const changed = state.order !== prev.order ||
                state.byId !== prev.byId ||
                state.conversation !== prev.conversation ||
                state.isOpen !== prev.isOpen ||
                state.draft !== prev.draft;
            if (changed && timer === undefined)
                timer = setTimeout(flush, PERSIST_DEBOUNCE_MS);
        });
        const onLeave = () => {
            if (timer !== undefined)
                flush();
        };
        const onVisibility = () => {
            if (document.visibilityState === 'hidden')
                onLeave();
        };
        // `pagehide` covers navigation; `visibilitychange` covers mobile browsers
        // that are backgrounded and then killed without ever firing it.
        if (typeof window !== 'undefined') {
            window.addEventListener('pagehide', onLeave);
            document.addEventListener('visibilitychange', onVisibility);
        }
        this.stopPersisting = () => {
            onLeave();
            unsubscribe();
            if (typeof window !== 'undefined') {
                window.removeEventListener('pagehide', onLeave);
                document.removeEventListener('visibilitychange', onVisibility);
            }
        };
    }
    /**
     * Watches for a conversation the visitor has walked away from: warns, then
     * locks. See `InactivityConfig` for why the widget does this at all when the
     * backend has a timer of its own.
     *
     * A TIMER ALONE IS NOT ENOUGH. Browsers throttle timers in background tabs
     * and suspend them outright when a laptop sleeps, so a 10-minute `setTimeout`
     * can fire late by any amount. Every decision is therefore taken from
     * `lastActivityAt` — wall clock — and re-checked when the tab comes back.
     */
    startIdleWatch() {
        if (typeof window === 'undefined')
            return;
        const recheck = () => {
            if (document.visibilityState === 'visible')
                this.checkIdle();
        };
        document.addEventListener('visibilitychange', recheck);
        window.addEventListener('focus', recheck);
        this.stopIdleWatch = () => {
            clearTimeout(this.idleTimer);
            this.idleTimer = undefined;
            document.removeEventListener('visibilitychange', recheck);
            window.removeEventListener('focus', recheck);
        };
        this.scheduleIdleCheck();
    }
    /** The conversation spoke: back to `live`, and the clock starts over. */
    markActivity = () => {
        this.lastActivityAt = Date.now();
        if (this.get().inactivity !== 'live' && this.get().closedBy !== 'team')
            this.set({ inactivity: 'live' });
        this.scheduleIdleCheck();
    };
    scheduleIdleCheck() {
        if (!this.config.inactivity.enabled || typeof window === 'undefined')
            return;
        clearTimeout(this.idleTimer);
        const { warnAfterMs, closeAfterWarningMs } = this.config.inactivity;
        const phase = this.get().inactivity;
        // Locked is the end of the line: nothing but reset() moves it.
        if (phase === 'closed')
            return;
        const due = phase === 'warned' ? warnAfterMs + closeAfterWarningMs : warnAfterMs;
        const wait = Math.max(0, this.lastActivityAt + due - Date.now());
        this.idleTimer = setTimeout(this.checkIdle, wait);
    }
    checkIdle = () => {
        if (!this.config.inactivity.enabled)
            return;
        const { warnAfterMs, closeAfterWarningMs } = this.config.inactivity;
        if (this.get().inactivity === 'closed')
            return;
        // A TURN IN FLIGHT IS NOT SILENCE, and turns here can run for minutes. No
        // timer is left behind: send()'s `finally` marks activity either way, which
        // reschedules this check.
        if (this.get().isSending)
            return;
        const silent = Date.now() - this.lastActivityAt;
        if (silent >= warnAfterMs + closeAfterWarningMs) {
            this.set({ inactivity: 'closed', closedBy: 'inactivity', isAgentTyping: false, agentStatus: undefined, agentStatusText: undefined });
            this.logger.info('chat locked after inactivity', { silentMs: silent });
            return;
        }
        if (silent >= warnAfterMs && this.get().inactivity === 'live') {
            this.set({ inactivity: 'warned' });
            this.logger.debug('inactivity warning shown', { silentMs: silent });
        }
        this.scheduleIdleCheck();
    };
    persistNow() {
        const state = this.get();
        writeUiState({ isOpen: state.isOpen, draft: state.draft }, this.ns);
        // A placeholder id isn't a chat yet: there is nothing the server could resume.
        // Data mode keeps no copy in the browser: its answers are the person's
        // business figures, and the server hands them back on resume.
        if (state.conversation && state.conversation.metadata?.pending !== true && this.config.mode !== 'data') {
            writeTranscript(state.conversation.id, selectMessages(state), this.ns);
        }
    }
    get actions() {
        return {
            init: this.init,
            send: this.send,
            retryLast: this.retryLast,
            cancel: this.cancel,
            setDraft: this.setDraft,
            rate: this.rate,
            handoff: this.handoff,
            downloadResult: this.downloadResult,
            reset: this.reset,
            open: this.open,
            close: this.close,
            toggle: this.toggle,
            showView: this.showView,
            loadPastChats: this.loadPastChats,
            openConversation: this.openConversation,
            deletePastChat: this.deletePastChat,
            dispose: this.dispose,
        };
    }
    /**
     * Re-attach to a stored conversation. Any failure — ended chat, cleared
     * server state, an endpoint that is down — resolves to `undefined` so init
     * falls through and starts a fresh conversation rather than erroring out.
     *
     * `local` is the transcript this browser stored on the previous page. It
     * is merged with the server's rather than trusted, see `mergeTranscript`.
     */
    tryResume = async (restoredId, local, epoch, reopen = false) => {
        const log = this.logger.scope('resume');
        try {
            const conversation = this.transport.resume
                ? await this.transport.resume(restoredId, { reopen })
                : { id: restoredId, createdAt: Date.now(), status: 'open' };
            if (epoch !== this.epoch)
                return undefined;
            if (!conversation) {
                // The transport logs the specific reason; this records the outcome.
                log.info('refused by transport, starting a new conversation', { chatId: restoredId });
                return undefined;
            }
            const server = (await this.transport.loadHistory?.(conversation.id)) ?? [];
            if (epoch !== this.epoch)
                return undefined;
            // A transport with no server-side transcript (the mock, for one) returns
            // nothing, and the local copy is then all there is.
            const history = mergeTranscript(local, server);
            // An empty transcript is not worth resuming into — it would look
            // identical to a new chat but silently reuse the old id.
            if (!history.length) {
                log.warn('transcript came back empty, starting a new conversation', {
                    chatId: conversation.id,
                });
                return undefined;
            }
            this.set({
                conversation,
                order: history.map((m) => m.id),
                byId: Object.fromEntries(history.map((m) => [m.id, m])),
            });
            if (this.config.features.persistence)
                storeId(conversation.id, this.ns);
            log.debug('resumed', {
                chatId: conversation.id,
                messages: history.length,
                local: local.length,
                server: server.length,
            });
            return conversation;
        }
        catch (err) {
            log.warn('failed, starting a new conversation', {
                chatId: restoredId,
                error: err instanceof Error ? err.message : String(err),
            });
            return undefined;
        }
    };
    /**
     * Prepends the configured welcome message. Rendered entirely client-side and
     * never sent to a transport, so opening the widget costs nothing. Idempotent
     * via a fixed id, and skipped when the transcript already opens with an
     * assistant turn (a resumed chat, or a transport that greets on its own).
     */
    showGreeting = () => {
        const greeting = this.config.greeting;
        if (!greeting?.text?.trim())
            return;
        const state = this.get();
        if (state.byId[GREETING_ID])
            return;
        const first = state.byId[state.order[0]];
        if (first && first.role === 'assistant')
            return;
        // The chips answer the opening question. In a resumed chat the visitor
        // has moved past it, and they would resurface under later replies.
        const resumed = state.order.some((id) => state.byId[id]?.role === 'user');
        const now = Date.now();
        const message = {
            id: GREETING_ID,
            conversationId: state.conversation?.id ?? '',
            role: 'assistant',
            content: greeting.text,
            status: 'complete',
            createdAt: now,
            updatedAt: now,
            suggestions: resumed
                ? undefined
                : greeting.suggestions?.map((label, index) => ({
                    id: `greeting-${index}`,
                    label,
                })),
            metadata: { local: true },
        };
        this.set((s) => ({
            order: [GREETING_ID, ...s.order],
            byId: { ...s.byId, [GREETING_ID]: message },
        }));
    };
    set = (patch) => {
        this.store.setState(patch);
    };
    get = () => this.store.getState();
    init = async () => {
        if (this.get().status === 'ready')
            return;
        // Mount effects, open() and send() can all race to initialise; they must
        // all await the same handshake rather than short-circuit past a pending one.
        if (this.initPromise)
            return this.initPromise;
        const promise = this.runInit().finally(() => {
            // reset() mid-handshake starts a new one; don't clear that one's slot.
            if (this.initPromise === promise)
                this.initPromise = undefined;
        });
        this.initPromise = promise;
        return promise;
    };
    runInit = async () => {
        const epoch = this.epoch;
        // True once reset() has moved on: this handshake's results are discarded.
        const stale = () => epoch !== this.epoch;
        this.set({ status: 'connecting', error: undefined });
        try {
            if (this.transport.connect && !this.connected) {
                await this.transport.connect(this.apply);
                this.connected = true;
            }
            if (stale())
                return;
            // A past chat picked from the history wins over whatever was stored.
            const requested = this.reopenId;
            this.reopenId = undefined;
            const restoredId = requested ??
                (this.config.features.persistence
                    ? readStoredId(this.config.persistenceTtlMs, this.logger, this.ns)
                    : undefined);
            if (!this.config.features.persistence) {
                // A reload is a new conversation, so nothing from the previous one
                // should linger. Flushing here also cleans up anything written while
                // persistence was enabled.
                const flushed = clearStoredId(this.ns);
                clearTranscript(this.ns);
                clearUiState(this.ns);
                this.logger
                    .scope('resume')
                    .debug(flushed
                    ? 'persistence disabled — flushed the previous conversation'
                    : 'persistence disabled — starting a new conversation');
            }
            else if (!restoredId && !requested) {
                this.logger
                    .scope('resume')
                    .debug('no stored conversation (first visit, or the stored one expired)');
            }
            // Repaint the previous page's transcript straight away, so a page change
            // doesn't flash an empty panel. The server check below confirms it, adds
            // anything newer, or replaces it if the chat has ended.
            const local = restoredId && this.config.mode !== 'data' ? readTranscript(restoredId, this.ns) : [];
            if (local.length) {
                this.set({
                    order: local.map((m) => m.id),
                    byId: Object.fromEntries(local.map((m) => [m.id, m])),
                });
            }
            let conversation = restoredId
                ? await this.tryResume(restoredId, local, epoch, restoredId === requested)
                : undefined;
            if (stale())
                return;
            // Don't repaint a transcript the server just refused on every page after
            // this one. The id is kept: a network blip shouldn't cost the chat, and
            // the first message of a new chat overwrites it anyway.
            if (restoredId && !conversation)
                clearTranscript(this.ns);
            if (!conversation) {
                // Whether this hits the network is the transport's call — a deferring
                // one logs that it is waiting for the first message.
                this.logger.info('no conversation to resume, initialising a new one');
                conversation = await this.transport.createConversation({
                    context: this.config.context,
                    locale: this.config.locale,
                });
                if (stale())
                    return;
                // A transport that defers creation hands back a placeholder id. Storing
                // it would make the next reload try to resume a chat that was never
                // created; the real id is persisted when `conversation.update` lands.
                const pending = conversation.metadata?.pending === true;
                if (this.config.features.persistence && !pending)
                    storeId(conversation.id, this.ns);
                const history = (await this.transport.loadHistory?.(conversation.id)) ?? [];
                if (stale())
                    return;
                this.set({
                    conversation,
                    order: history.map((m) => m.id),
                    byId: Object.fromEntries(history.map((m) => [m.id, m])),
                });
            }
            this.showGreeting();
            this.set({ status: 'ready' });
            // ARRIVING COUNTS AS ACTIVITY, however old the transcript is. Ageing a
            // resumed chat by its last message instead would lock out every returning
            // visitor — and the way back is reset(), which mints a new chat id: the
            // thread's memory gone, and the CRM lead delivered a second time rather
            // than updated. The backend already handles this properly, opening a new
            // sitting on the same thread, so the widget must let them type.
            this.markActivity();
            this.events.emit('ready', { conversationId: conversation.id });
        }
        catch (err) {
            if (stale())
                return;
            const error = toChatError(err);
            this.set({ status: 'error', error });
            this.events.emit('error', { error });
        }
    };
    send = async (text) => {
        const trimmed = text.trim();
        const { conversation, isSending, isAgentTyping, inactivity } = this.get();
        // ONE QUESTION AT A TIME, in every mode: while a reply is on its way —
        // the AI answering, the AI drafting for a person, or a person typing —
        // the visitor waits for it. The dots that mean "on its way" all expire on
        // their own, so this never locks anyone out for good.
        if (!trimmed || isSending || isAgentTyping)
            return;
        // The composer is already locked; this catches the embed API's
        // sendMessage(), which does not go through it.
        if (inactivity === 'closed') {
            this.logger.warn('message dropped: the chat was closed after inactivity. Call reset() first.');
            return;
        }
        if (!conversation) {
            await this.init();
            if (!this.get().conversation)
                return;
        }
        const conversationId = this.get().conversation.id;
        const now = Date.now();
        const message = {
            id: createId('msg'),
            conversationId,
            role: 'user',
            content: trimmed,
            status: 'complete',
            createdAt: now,
            updatedAt: now,
        };
        this.lastUserText = trimmed;
        this.markActivity();
        // From Home (a prompt card) or the host page's own button: show the answer.
        this.set({ view: 'chat' });
        this.appendMessage(message);
        // Clear stale suggestions so the chips don't linger under an answered turn.
        this.clearSuggestions();
        this.set({ isSending: true, draft: '', error: undefined });
        this.events.emit('message:sent', { message });
        const replyId = createId('msg');
        this.controller = new AbortController();
        // A reply that arrives after reset() belongs to the abandoned chat.
        const epoch = this.epoch;
        const emit = (event) => {
            if (epoch === this.epoch)
                this.apply(event);
        };
        try {
            await this.transport.sendMessage({ conversationId, message, replyId, context: this.config.context }, emit, this.controller.signal);
        }
        catch (err) {
            emit({ type: 'message.error', messageId: replyId, error: toChatError(err) });
        }
        finally {
            if (epoch === this.epoch) {
                this.set({ isSending: false, isAgentTyping: false, agentStatus: undefined, agentStatusText: undefined });
                // However the turn ended — reply, error, or the visitor pressing stop —
                // the silence starts here, and the check skipped while it was in flight
                // needs rescheduling.
                this.markActivity();
            }
        }
    };
    retryLast = async () => {
        const { order, byId } = this.get();
        // Drop the failed assistant turn before re-asking.
        const failedId = [...order].reverse().find((id) => byId[id]?.status === 'failed');
        if (failedId)
            this.removeMessage(failedId);
        const text = this.lastUserText;
        this.set({ error: undefined });
        if (text)
            await this.send(text);
    };
    cancel = () => {
        this.controller?.abort();
        this.set({ isSending: false, isAgentTyping: false, agentStatus: undefined, agentStatusText: undefined });
    };
    setDraft = (draft) => this.set({ draft });
    rate = async (messageId, value) => {
        const current = this.get().byId[messageId];
        if (!current)
            return;
        const next = current.feedback === value ? null : value;
        this.patchMessage(messageId, { feedback: next });
        if (next && this.transport.sendFeedback && this.get().conversation) {
            await this.transport
                .sendFeedback({ conversationId: this.get().conversation.id, messageId, value: next })
                .catch(() => this.patchMessage(messageId, { feedback: current.feedback ?? null }));
        }
    };
    downloadResult = async (resultId) => {
        const conversation = this.get().conversation;
        if (!conversation || !this.transport.downloadResult)
            return false;
        try {
            const blob = await this.transport.downloadResult(conversation.id, resultId);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `data-${resultId.slice(0, 8)}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 10_000);
            return true;
        }
        catch (err) {
            this.logger.warn('download failed', { resultId, error: String(err) });
            return false;
        }
    };
    handoff = async (reason) => {
        const conversation = this.get().conversation;
        if (!conversation || !this.transport.requestHandoff)
            return;
        await this.transport.requestHandoff(conversation.id, reason);
        this.set({ conversation: { ...conversation, status: 'handed_off' } });
        this.appendMessage({
            id: createId('msg'),
            conversationId: conversation.id,
            role: 'system',
            content: "You're in the queue for a teammate. They'll pick this up shortly.",
            status: 'complete',
            createdAt: Date.now(),
            updatedAt: Date.now(),
        });
    };
    /**
     * Start a brand-new conversation. Safe at any moment: mid-reply, mid-resume,
     * or twice in a row. Anything still in flight is cancelled and its late
     * results are discarded (see `epoch`), so the new chat starts clean.
     */
    reset = async () => {
        this.epoch++;
        this.cancel();
        clearStoredId(this.ns);
        clearTranscript(this.ns);
        this.lastUserText = undefined;
        // Detach from a handshake that may still be running for the old chat.
        this.initPromise = undefined;
        this.set({
            conversation: undefined,
            order: [],
            byId: {},
            status: 'idle',
            error: undefined,
            draft: '',
            isSending: false,
            isAgentTyping: false, agentStatus: undefined, agentStatusText: undefined,
            unreadCount: 0,
            inactivity: 'live',
            closedBy: undefined,
        });
        this.markActivity();
        await this.init();
    };
    /** Always onto the chat; Messages and Home are a back arrow away. */
    open = () => {
        this.set({ isOpen: true, unreadCount: 0, view: 'chat' });
        this.events.emit('open', undefined);
        void this.init();
    };
    close = () => {
        this.set({ isOpen: false });
        this.events.emit('close', undefined);
    };
    toggle = () => (this.get().isOpen ? this.close() : this.open());
    showView = (view) => {
        const allowed = view === 'chat' || (view === 'history' ? this.historyAvailable() : Boolean(this.config.home));
        this.set({ view: allowed ? view : 'chat' });
        if (allowed && view === 'history')
            void this.loadPastChats();
    };
    /** Data mode with a transport that lists the person's private chats. */
    historyAvailable = () => this.config.mode === 'data' && this.config.features.history && Boolean(this.transport.listPastChats);
    loadPastChats = async () => {
        if (!this.historyAvailable() || !this.transport.listPastChats)
            return;
        this.set((s) => ({ pastChats: { ...s.pastChats, status: 'loading' } }));
        try {
            const items = await this.transport.listPastChats();
            this.set({ pastChats: { status: 'ready', items } });
        }
        catch (err) {
            this.logger.warn('could not list past chats', { error: err instanceof Error ? err.message : String(err) });
            this.set((s) => ({ pastChats: { ...s.pastChats, status: 'error' } }));
        }
    };
    openConversation = async (conversationId) => {
        if (this.get().conversation?.id === conversationId) {
            this.set({ view: 'chat' });
            return;
        }
        this.reopenId = conversationId;
        this.set({ view: 'chat' });
        // reset() moves to a new epoch and re-runs init, which opens `reopenId`.
        await this.reset();
    };
    deletePastChat = async (conversationId) => {
        if (!this.transport.deletePastChat)
            return false;
        try {
            await this.transport.deletePastChat(conversationId);
        }
        catch (err) {
            this.logger.warn('could not delete a past chat', { error: err instanceof Error ? err.message : String(err) });
            return false;
        }
        this.set((s) => ({
            pastChats: { ...s.pastChats, items: s.pastChats.items.filter((c) => c.id !== conversationId) },
        }));
        if (this.get().conversation?.id === conversationId)
            await this.reset();
        return true;
    };
    dispose = () => {
        // Flushes first: a framework unmount (React StrictMode, a route that
        // removes the widget) is a page change as far as the visitor is concerned.
        this.stopPersisting?.();
        this.stopPersisting = undefined;
        this.stopIdleWatch?.();
        this.stopIdleWatch = undefined;
        this.cancel();
        this.transport.dispose?.();
        this.events.clear();
    };
    /** Swap the backend at runtime (mock -> REST -> WebSocket) without remounting. */
    setTransport(transport) {
        this.transport.dispose?.();
        this.transport = transport;
        this.connected = false;
        this.config = { ...this.config, transport };
        return this.reset();
    }
    getConfig() {
        return this.config;
    }
    /** The one place transport events become state. */
    apply = (event) => {
        switch (event.type) {
            case 'message.start':
                this.appendMessage(event.message);
                break;
            case 'message.delta': {
                const existing = this.get().byId[event.messageId];
                if (!existing) {
                    // Delta before start (out-of-order WS frame): synthesise the shell.
                    this.appendMessage({
                        id: event.messageId,
                        conversationId: this.get().conversation?.id ?? '',
                        role: 'assistant',
                        content: event.delta,
                        status: 'streaming',
                        createdAt: Date.now(),
                        updatedAt: Date.now(),
                    });
                    break;
                }
                this.patchMessage(event.messageId, {
                    content: existing.content + event.delta,
                    status: 'streaming',
                });
                break;
            }
            case 'message.pause': {
                // Shown in full so far, nothing arriving: drop the cursor. A later
                // delta sets it streaming again; message.complete finishes it.
                if (this.get().byId[event.messageId]?.status === 'streaming') {
                    this.patchMessage(event.messageId, { status: 'pending' });
                }
                break;
            }
            case 'message.blocks':
                this.patchMessage(event.messageId, { blocks: event.blocks });
                break;
            case 'message.sources':
                this.patchMessage(event.messageId, { sources: event.sources });
                break;
            case 'message.suggestions':
                this.patchMessage(event.messageId, { suggestions: event.suggestions });
                break;
            case 'message.actions':
                this.patchMessage(event.messageId, { actions: event.actions });
                break;
            case 'message.complete': {
                this.patchMessage(event.messageId, { ...event.message, status: 'complete' });
                const message = this.get().byId[event.messageId];
                if (message)
                    this.events.emit('message:received', { message });
                if (!this.get().isOpen)
                    this.set((s) => ({ unreadCount: s.unreadCount + 1 }));
                // Also covers a transport that pushes a reply outside send() — a
                // WebSocket frame, or an agent speaking first.
                this.markActivity();
                break;
            }
            case 'message.error': {
                if (event.messageId && this.get().byId[event.messageId]) {
                    this.patchMessage(event.messageId, { status: 'failed', error: event.error });
                }
                else if (event.messageId) {
                    this.appendMessage({
                        id: event.messageId,
                        conversationId: this.get().conversation?.id ?? '',
                        role: 'assistant',
                        content: '',
                        status: 'failed',
                        createdAt: Date.now(),
                        updatedAt: Date.now(),
                        error: event.error,
                    });
                }
                this.set({ error: event.error, isAgentTyping: false, agentStatus: undefined, agentStatusText: undefined });
                this.events.emit('error', { error: event.error });
                break;
            }
            case 'typing':
                this.set(event.isTyping ? { isAgentTyping: true } : { isAgentTyping: false, agentStatus: undefined, agentStatusText: undefined });
                break;
            case 'status':
                // A step with no line of its own keeps the last one: "Checking pricing…"
                // must not blink back to a canned line between two of the server's.
                this.set({
                    isAgentTyping: true,
                    agentStatus: event.stage,
                    // A person typing in the console is only dots: no AI line carries over.
                    ...(event.text ? { agentStatusText: event.text } : event.stage === 'typing' ? { agentStatusText: undefined } : {}),
                });
                break;
            case 'conversation.update': {
                const current = this.get().conversation;
                if (!current)
                    break;
                const next = { ...current, ...event.conversation };
                this.set({ conversation: next });
                // Ended by the team: lock it, with the way to a new chat.
                if (event.conversation.status === 'closed' && current.status !== 'closed') {
                    clearTimeout(this.idleTimer);
                    this.set({ inactivity: 'closed', closedBy: 'team', isAgentTyping: false, agentStatus: undefined, agentStatusText: undefined });
                    this.logger.info('chat ended by the team');
                }
                // A transport that creates the chat lazily (the id only exists after
                // the first message) reports the real id here — re-persist it, and
                // re-home any messages still carrying the placeholder.
                if (next.id !== current.id) {
                    if (this.config.features.persistence)
                        storeId(next.id, this.ns);
                    this.set((state) => ({
                        byId: Object.fromEntries(Object.entries(state.byId).map(([id, message]) => [
                            id,
                            message.conversationId === current.id ? { ...message, conversationId: next.id } : message,
                        ])),
                    }));
                }
                break;
            }
        }
    };
    appendMessage(message) {
        this.set((s) => ({
            order: s.order.includes(message.id) ? s.order : [...s.order, message.id],
            byId: { ...s.byId, [message.id]: message },
        }));
    }
    patchMessage(id, patch) {
        this.set((s) => {
            const current = s.byId[id];
            if (!current)
                return {};
            return { byId: { ...s.byId, [id]: { ...current, ...patch, updatedAt: Date.now() } } };
        });
    }
    removeMessage(id) {
        this.set((s) => {
            const { [id]: _removed, ...rest } = s.byId;
            return { order: s.order.filter((x) => x !== id), byId: rest };
        });
    }
    clearSuggestions() {
        this.set((s) => {
            const byId = { ...s.byId };
            let changed = false;
            for (const id of s.order) {
                if (byId[id]?.suggestions?.length) {
                    byId[id] = { ...byId[id], suggestions: undefined };
                    changed = true;
                }
            }
            return changed ? { byId } : {};
        });
    }
}
/*
 * Selectors MUST be referentially stable for unchanged state.
 * zustand v5's `useStore` is a bare `useSyncExternalStore` with no equality
 * function, so a selector that allocates a fresh array on every call makes
 * React re-render without end ("Maximum update depth exceeded").
 */
const EMPTY_SUGGESTIONS = Object.freeze([]);
/* Keyed on the `order` array itself rather than a single shared slot, so two
 * widget instances on one page don't evict each other's entry. Both `order`
 * and `byId` are replaced immutably by the engine, so reference equality on
 * the pair is an exact "nothing changed" test. */
const messagesCache = new WeakMap();
export function selectMessages(state) {
    const cached = messagesCache.get(state.order);
    if (cached && cached.byId === state.byId)
        return cached.result;
    const result = state.order.map((id) => state.byId[id]).filter(Boolean);
    messagesCache.set(state.order, { byId: state.byId, result });
    return result;
}
/** Chips from the newest assistant message only.
 *  Returns the message's own array, or a shared frozen empty one — never a
 *  fresh `[]`, which would defeat the snapshot check. */
export function selectSuggestions(state) {
    for (let i = state.order.length - 1; i >= 0; i--) {
        const message = state.byId[state.order[i]];
        if (message?.role === 'assistant' && message.suggestions?.length)
            return message.suggestions;
    }
    return EMPTY_SUGGESTIONS;
}
export function selectRetryableError(state) {
    return state.error?.retryable ? state.error : undefined;
}
export { chatError };
function isMobileViewport() {
    try {
        return window.matchMedia(MOBILE_QUERY).matches;
    }
    catch {
        return false;
    }
}
